// frontend/src/pages/Sabesp/components/GroupManagerModal.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import ConfirmDeleteModal2 from "../../../components/DeletAssitant";
import {
  listGroupDocuments, uploadGroupDocument, deleteGroupDocument, renameGroup, deleteGroup,
} from "../api/sabespClient.js";

const POLL_INTERVAL_MS = 3000;

// Etapas do processamento em segundo plano (job_state devolvido pela Function).
const JOB_STEP_LABEL = {
  recebido: "Na fila",
  extraindo: "Lendo as páginas",
  trechos: "Indexando o texto",
  figuras: "Catalogando figuras",
  gravando: "Gravando",
};

function formatSize(bytes) {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

function isPdf(file) {
  return /\.pdf$/i.test(file.name || "");
}

export default function GroupManagerModal({ group, token, onClose, onRenamed, onDeleted }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(group.name);
  const [dragOver, setDragOver] = useState(false);
  // Arquivos escolhidos pelo usuario que ainda nao viraram documento no servidor:
  // { key, name, size, status: "fila" | "enviando" | "erro", error }
  const [uploads, setUploads] = useState([]);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(false);
  // Feedback imediato: o estado muda no clique, sem esperar o servidor.
  const [confirmingId, setConfirmingId] = useState(null); // linha pedindo "Excluir? Cancelar"
  const [deletingIds, setDeletingIds] = useState([]); // arquivos sendo excluidos
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const pendingRef = useRef([]);
  const runningRef = useRef(false);
  const mountedRef = useRef(true);

  const loadDocuments = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true);
      return listGroupDocuments(group.id, token)
        .then((items) => {
          if (mountedRef.current) setDocuments(items);
        })
        .catch((err) => {
          if (mountedRef.current && !silent) setError(err.message);
        })
        .finally(() => {
          if (mountedRef.current && !silent) setLoading(false);
        });
    },
    [group.id, token]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadDocuments();
    return () => {
      mountedRef.current = false;
      pendingRef.current = [];
    };
  }, [loadDocuments]);

  // Enquanto algum arquivo ainda esta sendo processado no servidor, acompanha o andamento.
  const hasProcessing = documents.some((doc) => doc.status === "processando");
  useEffect(() => {
    if (!hasProcessing) return undefined;
    const timer = setInterval(() => loadDocuments(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasProcessing, loadDocuments]);

  async function saveRename() {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === group.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await renameGroup(group.id, trimmed, token);
      onRenamed(group.id, trimmed);
      setEditingName(false);
    } catch (err) {
      setError(err.message);
    } finally {
      if (mountedRef.current) setSavingName(false);
    }
  }

  function patchUpload(key, changes) {
    if (mountedRef.current) setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, ...changes } : u)));
  }

  function removeUpload(key) {
    setUploads((prev) => prev.filter((u) => u.key !== key));
  }

  // Envia um arquivo por vez (fila): o servidor so recebe e enfileira, entao cada envio e rapido;
  // o processamento de cada um segue em segundo plano e aparece na lista de arquivos.
  async function runQueue() {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      while (mountedRef.current && pendingRef.current.length > 0) {
        const { key, file } = pendingRef.current.shift();
        patchUpload(key, { status: "enviando" });
        try {
          const result = await uploadGroupDocument(group.id, file, token);
          if (result && result.duplicate && mountedRef.current) {
            setNotice(`"${file.name}" já está neste grupo.`);
          }
          if (mountedRef.current) removeUpload(key);
        } catch (err) {
          patchUpload(key, { status: "erro", error: err.message });
        }
        await loadDocuments(true);
      }
    } finally {
      runningRef.current = false;
    }
  }

  function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setError(null);
    setNotice(null);
    const stamp = Date.now();
    const items = files.map((file, index) => ({
      key: `${stamp}-${index}-${file.name}`,
      file,
    }));
    setUploads((prev) => [
      ...prev,
      ...items.map(({ key, file }) =>
        isPdf(file)
          ? { key, name: file.name, size: file.size, status: "fila", error: null }
          : { key, name: file.name, size: file.size, status: "erro", error: "Somente arquivos PDF." }
      ),
    ]);
    pendingRef.current.push(...items.filter(({ file }) => isPdf(file)));
    runQueue();
  }

  async function handleDeleteFile(documentId) {
    setConfirmingId(null);
    setError(null);
    setDeletingIds((prev) => [...prev, documentId]);
    try {
      await deleteGroupDocument(group.id, documentId, token);
      // Sai da lista assim que o servidor confirma, sem esperar uma nova consulta.
      if (mountedRef.current) setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    } catch (err) {
      if (mountedRef.current) setError(`Não foi possível excluir o arquivo: ${err.message}`);
    } finally {
      if (mountedRef.current) setDeletingIds((prev) => prev.filter((id) => id !== documentId));
    }
  }

  async function handleDeleteGroup() {
    setDeletingGroup(true);
    setError(null);
    try {
      await deleteGroup(group.id, token);
      onDeleted(group.id);
    } catch (err) {
      if (mountedRef.current) {
        setError(`Não foi possível excluir o grupo: ${err.message}`);
        setDeletingGroup(false);
      }
    }
  }

  const uploadingNow = uploads.some((u) => u.status === "fila" || u.status === "enviando");

  return (
    <div className="sabesp-group-modal-overlay" onClick={onClose}>
      <div className="sabesp-group-modal" onClick={(e) => e.stopPropagation()}>
        {deletingGroup && (
          <div className="sabesp-group-deleting-overlay" role="alert" aria-live="assertive">
            <span className="sabesp-spinner sabesp-spinner-lg" />
            <strong>Excluindo o grupo "{group.name}"...</strong>
            <span>Apagando {documents.length} arquivo(s). Pode levar alguns segundos; não feche esta janela.</span>
          </div>
        )}
        <div className="sabesp-group-modal-header">
          {editingName ? (
            <input
              className="sabesp-group-name-input"
              autoFocus
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveRename()}
              onBlur={saveRename}
              disabled={savingName}
            />
          ) : (
            <>
              <h3>{group.name}</h3>
              <button className="sabesp-group-edit-btn" title="Renomear" onClick={() => setEditingName(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                </svg>
              </button>
            </>
          )}
          {savingName && <span className="sabesp-group-saving"><span className="sabesp-spinner" /> Salvando...</span>}
          <button className="sabesp-group-close-btn" onClick={onClose} disabled={deletingGroup}>&times;</button>
        </div>

        {error && <div className="sabesp-error-box sabesp-error-box-general">{error}</div>}
        {notice && <div className="sabesp-group-notice">{notice}</div>}

        <div
          className={`sabesp-group-dropzone${dragOver ? " dragover" : ""}`}
          onClick={() => document.getElementById("sabesp-group-file-input").click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <strong>
            {uploadingNow ? (<><span className="sabesp-spinner" /> Enviando...</>) : "Clique ou arraste um ou mais PDFs aqui"}
          </strong>
          <input
            id="sabesp-group-file-input"
            type="file"
            accept=".pdf"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        <div className="sabesp-group-file-list">
          {uploads.map((u) => (
            <div className={`sabesp-group-file-row${u.status === "erro" ? " is-error" : ""}`} key={u.key}>
              <div className="sabesp-group-file-main">
                <span className="sabesp-group-file-name" title={u.name}>{u.name}</span>
                {u.status === "erro" && <span className="sabesp-group-file-error">{u.error}</span>}
              </div>
              <span className="sabesp-group-file-status">
                {u.status === "enviando" && <span className="sabesp-spinner" />}{" "}
                {u.status === "fila" ? "Na fila" : u.status === "enviando" ? "Enviando..." : "Erro"}
              </span>
              {u.status === "erro" && (
                <button className="sabesp-group-file-del" title="Descartar" onClick={() => removeUpload(u.key)}>&times;</button>
              )}
            </div>
          ))}

          {loading ? (
            <div className="sabesp-group-file-empty"><span className="sabesp-spinner" /> Carregando arquivos...</div>
          ) : documents.length === 0 && uploads.length === 0 ? (
            <div className="sabesp-group-file-empty">Nenhum arquivo neste grupo ainda.</div>
          ) : (
            documents.map((doc) => {
              const processing = doc.status === "processando";
              const failed = doc.status === "erro";
              const progress = Math.max(0, Math.min(100, Number(doc.job_progress) || 0));
              const deleting = deletingIds.includes(doc.id);
              const confirming = confirmingId === doc.id && !deleting;
              return (
                <div
                  className={`sabesp-group-file-row${failed ? " is-error" : ""}${deleting ? " is-deleting" : ""}`}
                  key={doc.id}
                  aria-busy={deleting}
                >
                  <div className="sabesp-group-file-main">
                    <span className="sabesp-group-file-name" title={doc.name}>{doc.name}</span>
                    {processing && (
                      <div className="sabesp-group-progress" title={`${progress}%`}>
                        <div className="sabesp-group-progress-bar" style={{ width: `${progress}%` }} />
                      </div>
                    )}
                    {failed && (
                      <span className="sabesp-group-file-error">
                        {doc.job_error || "Falha ao processar o arquivo. Exclua e envie novamente."}
                      </span>
                    )}
                  </div>
                  {deleting ? (
                    <span className="sabesp-group-file-status"><span className="sabesp-spinner" /> Excluindo...</span>
                  ) : confirming ? (
                    <span className="sabesp-group-confirm">
                      <span>Excluir este arquivo?</span>
                      <button className="sabesp-group-confirm-yes" onClick={() => handleDeleteFile(doc.id)}>Excluir</button>
                      <button className="sabesp-group-confirm-no" onClick={() => setConfirmingId(null)}>Cancelar</button>
                    </span>
                  ) : (
                    <>
                      <span className="sabesp-group-file-status">
                        {processing
                          ? `${JOB_STEP_LABEL[doc.job_state] || "Processando"} ${progress}%`
                          : failed
                          ? "Erro"
                          : formatSize(doc.size_bytes)}
                      </span>
                      <button
                        className="sabesp-group-file-del"
                        title={failed ? "Excluir (depois é possível enviar de novo)" : "Excluir arquivo do grupo"}
                        aria-label={`Excluir ${doc.name}`}
                        onClick={() => setConfirmingId(doc.id)}
                      >
                        &times;
                      </button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        <button
          className="sabesp-group-delete-all-btn"
          onClick={() => setConfirmDeleteGroup(true)}
          disabled={deletingGroup}
        >
          {deletingGroup ? (<><span className="sabesp-spinner" /> Excluindo grupo...</>) : "Excluir grupo inteiro"}
        </button>
        {deletingGroup && (
          <div className="sabesp-group-busy-note">Apagando os arquivos do grupo. Isso pode levar alguns segundos.</div>
        )}
      </div>

      <ConfirmDeleteModal2
        isOpen={confirmDeleteGroup}
        onClose={() => setConfirmDeleteGroup(false)}
        onConfirm={() => {
          setConfirmDeleteGroup(false);
          handleDeleteGroup();
        }}
        title="Excluir este grupo?"
        message={`Todos os ${documents.length} arquivo(s) deste grupo serão apagados permanentemente.`}
      />
    </div>
  );
}
